import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Row, Col, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';

const ReferralHistory = () => {
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    invited: 0,
    registered: 0,
    completed: 0,
    rewardsClaimed: 0
  });
  const [loading, setLoading] = useState(true);
  const [claimingReward, setClaimingReward] = useState(null);

  useEffect(() => {
    loadReferrals();
  }, []);

  const loadReferrals = async () => {
    try {
      setLoading(true);
      const [referralsRes, statsRes] = await Promise.all([
        axios.get('/referrals/my-referrals'),
        axios.get('/referrals/stats')
      ]);
      
      // Ensure referrals is always an array
      const referralsData = Array.isArray(referralsRes.data) ? referralsRes.data : [];
      setReferrals(referralsData);
      
      // Ensure stats is always an object with default values
      const statsData = statsRes.data && typeof statsRes.data === 'object' ? statsRes.data : {
        total: 0,
        invited: 0,
        registered: 0,
        completed: 0,
        rewardsClaimed: 0
      };
      setStats(statsData);
    } catch (error) {
      console.error('Error loading referrals:', error);
      toast.error('Failed to load referral data');
      // Set default values on error
      setReferrals([]);
      setStats({
        total: 0,
        invited: 0,
        registered: 0,
        completed: 0,
        rewardsClaimed: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClaimReward = async (referralId) => {
    try {
      setClaimingReward(referralId);
      await axios.post(`/referrals/claim-reward/${referralId}`);
      
      toast.success('Reward claimed successfully! You\'ve earned £25!');
      loadReferrals(); // Refresh data
    } catch (error) {
      console.error('Error claiming reward:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to claim reward');
      }
    } finally {
      setClaimingReward(null);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'secondary',
      invited: 'info',
      registered: 'primary',
      completed: 'success',
      expired: 'warning'
    };
    
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-primary">{stats.total}</h4>
              <small className="text-muted">Total Referrals</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-info">{stats.invited}</h4>
              <small className="text-muted">Invitations Sent</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-primary">{stats.registered}</h4>
              <small className="text-muted">Registered</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-success">{stats.completed}</h4>
              <small className="text-muted">Completed</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-warning">{stats.rewardsClaimed}</h4>
              <small className="text-muted">Rewards Claimed</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-success">£{stats.rewardsClaimed * 25}</h4>
              <small className="text-muted">Total Earned</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Referrals Table */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">My Referrals</h5>
        </Card.Header>
        <Card.Body>
                     {!Array.isArray(referrals) || referrals.length === 0 ? (
             <Alert variant="info">
               You haven't sent any referrals yet. Start by inviting a colleague!
             </Alert>
           ) : (
             <Table responsive striped>
               <thead>
                 <tr>
                   <th>Colleague</th>
                   <th>Email</th>
                   <th>Company</th>
                   <th>Status</th>
                   <th>Invited</th>
                   <th>Registered</th>
                   <th>Completed</th>
                   <th>Actions</th>
                 </tr>
               </thead>
               <tbody>
                 {referrals.map((referral) => (
                  <tr key={referral._id}>
                    <td>{referral.referredName}</td>
                    <td>{referral.referredEmail}</td>
                    <td>{referral.referredCompany}</td>
                    <td>{getStatusBadge(referral.status)}</td>
                    <td>{formatDate(referral.invitationSentAt)}</td>
                    <td>{formatDate(referral.registeredAt)}</td>
                    <td>{formatDate(referral.completedAt)}</td>
                    <td>
                      {referral.status === 'completed' && !referral.rewardClaimed && (
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => handleClaimReward(referral._id)}
                          disabled={claimingReward === referral._id}
                        >
                          {claimingReward === referral._id ? 'Claiming...' : 'Claim £25'}
                        </Button>
                      )}
                      {referral.rewardClaimed && (
                        <Badge bg="success">Reward Claimed</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default ReferralHistory;
